import { Observable, Subject, Subscriber, Subscription } from 'rxjs';
import { UrlTree } from '@angular/router';

/**
 * A subject that waits for execution until a subscriber is registered.
 *
 * Needed for guards in a compositeGuard, because otherwise they are executed before inspected.
 * The compositeGuard will collect all observables of all CanActivate methods first, and then resolve them sequentially, but stops when one returns false.
 * So that's why a guard shouldn't start their logic when canActivate is called, but when a subscription is made.
 *
 */
export class ColdSubject extends Subject<boolean | UrlTree> {
  constructor(private execution: (subject: ColdSubject) => void | Observable<boolean> | Promise<boolean> | boolean) {
    super();
  }

  _subscribe(subscriber: Subscriber<boolean>): Subscription {
    // @ts-ignore
    const subscription = super._subscribe(subscriber);
    this.execute();
    return subscription;
  }

  private execute() {
    try {
      const result = this.execution(this);
      if (ColdSubject.isObservable(result)) {
        let isCompleted = false; // Make sure it completes even if user forgets it.
        result.subscribe(
          {
            next: next => {
            this.next(next);
            this.complete();
            isCompleted = true;
          },
          error: error => this.error(error),
          complete: () => !isCompleted && this.complete()
        });
      } else if (ColdSubject.isPromise(result)) {
        let isCompleted = false; // Make sure it completes even if user forgets it.
        result.then(
          next => {
            this.next(next);
            this.complete();
            isCompleted = true;
          },
          error => this.error(error)
        );
      } else if (typeof result === 'boolean' || ColdSubject.isUrlTree(result)) {
        this.next(result);
        this.complete();
      } else {
        throw new Error('Result type not supported');
      }
    } catch (error) {
      this.error(error);
    }
  }

  private static isObservable(object: any): object is Observable<boolean> {
    return typeof object === 'object' && 'subscribe' in object;
  }

  private static isPromise(object: any): object is Promise<boolean> {
    return typeof object === 'object' && 'then' in object;
  }

  static isUrlTree(object: any): object is UrlTree {
    return typeof object === 'object' && object.__proto__?.constructor?.name === 'UrlTree';
  }
}
